/**
 * Young Professional Landing Screen
 * Persona-specific landing page for Maxime (31 ans, jeune professionnel)
 * URL: https://wealfi.co/fr/young-professional
 */

import React, { useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Pressable,
  Linking,
  useWindowDimensions,
  Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import useDomain from '../../hooks/useDomain';
import useWebSEO from '../../hooks/useWebSEO';
import { analytics, AnalyticsEvent } from '../../utils/analytics';
import { initialUtmParams } from '../../utils/initialUtm';


// Brand Colors (same as main landing)
const brandColors = {
  green: '#71B352',
  greenDark: '#5B8A3C',
  greenLight: '#E8F5E0',
  dark: '#3D4543',
  orange: '#D97234',
  orangeLight: '#FFF3EB',
  gray: '#6B7280',
  background: '#FAFBFC',
  white: '#FFFFFF',
};

// Persona-specific content
const PERSONA = {
  name: 'Maxime',
  age: 31,
  job: 'Product Manager',
  income: '3 800€/mois net',
  situation: 'CDI, locataire Paris',
};

// Recognition checklist for young professionals
const recognitionItems = [
  "Tu gagnes bien ta vie mais tu sais que tu pourrais faire mieux",
  "Tu as de l'épargne qui dort sur des livrets",
  "Tu te dis \"je m'en occuperai ce week-end\" depuis 6 mois",
  "Tu n'as pas envie de passer 10h à comparer des ETF",
];

// Pain points specific to young professionals
const painPoints = [
  {
    emoji: '💤',
    title: "Ton Livret A à 3%",
    description: "Pendant que l'inflation te coûte 5%. Tu perds de l'argent chaque mois.",
  },
  {
    emoji: '🤯',
    title: "L'info overload",
    description: "YouTube, Reddit, blogs... 50 avis différents. Tu finis par ne rien faire.",
  },
  {
    emoji: '⏰',
    title: "Pas le temps",
    description: "Entre le taf, le sport et les potes, tu n'as pas 10h pour devenir expert.",
  },
];

// Features specific to this persona
const features = [
  {
    icon: 'lightning-bolt',
    title: "Diagnostic en 3 min",
    desc: "Réponds à 5 questions. Wealfi analyse et te dit exactement où tu perds de l'argent.",
  },
  {
    icon: 'clipboard-check-outline',
    title: "Plan d'action concret",
    desc: "Pas de théorie. \"Ouvre un PEA chez X, mets Y€/mois sur Z.\" C'est tout.",
  },
  {
    icon: 'robot',
    title: "Assistant IA 24/7",
    desc: "\"C'est quoi un ETF ?\" Pose tes questions, obtiens des réponses claires.",
  },
];

// Testimonials
const testimonials = [
  {
    name: 'Thomas L.',
    role: 'Product Manager, 32 ans',
    text: "En 3 minutes, Wealfi a détecté que je perdais 168€/an sur un arbitrage Livret A → PEA. Je n'aurais jamais fait le calcul seul.",
    savings: '168€/an',
  },
  {
    name: 'Julie M.',
    role: 'Dev Full-Stack, 29 ans',
    text: "Je procrastinais depuis 2 ans. Wealfi m'a donné un plan en 3 étapes. Mon PEA est ouvert depuis 3 mois.",
    savings: 'PEA ouvert',
  },
];

// FAQ items
const faqItems = [
  {
    q: "C'est vraiment gratuit ?",
    a: "Le diagnostic est 100% gratuit. Tu peux ensuite passer Premium (9,99€/mois) pour un suivi illimité, mais ce n'est pas obligatoire.",
  },
  {
    q: "Vous avez accès à mes comptes ?",
    a: "Non. Tu réponds à 5 questions, c'est tout. La connexion bancaire est optionnelle et sécurisée par Powens, établissement de paiement agréé par l'ACPR (n°16948). Tes données ne sont jamais vendues et tu peux supprimer ton compte à tout moment.",
  },
  {
    q: "C'est du conseil financier ?",
    a: "Wealfi est un outil d'aide à la décision, pas un service de conseil financier réglementé au sens de l'AMF. On t'aide à comprendre et agir, mais la décision finale t'appartient.",
  },
];

// Premium features
const premiumFeatures = [
  "Chat illimité avec l'IA",
  "Objectifs personnalisés illimités",
  "Alertes opportunités en temps réel",
  "Contenu éducatif premium",
  "Support prioritaire",
];

export default function YoungProfessionalLandingScreen() {
  const navigation = useNavigation<any>();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const { isLandingDomain, appUrl } = useDomain();
  const pageLoadTime = useRef<number>(Date.now());

  useWebSEO({
    title: 'Wealfi — Fais fructifier ton salaire | Jeune professionnel',
    description: 'Tu gagnes bien ta vie mais ton épargne dort ? Wealfi t\'aide à optimiser tes finances en 3 minutes. Diagnostic gratuit pour les jeunes professionnels.',
    keywords: 'jeune professionnel finance, optimiser salaire, épargne jeune actif, investir premier emploi, gestion patrimoine débutant',
    ogUrl: 'https://wealfi.co/fr/young-professional',
    ogImage: 'https://wealfi.co/og-images/young-professional.png',
  });

  // UTM parameters captured at page load (before React Navigation can strip them)
  const getUtmParams = useCallback(() => ({
    ...initialUtmParams,
    persona: 'young_professional',
  }), []);

  // Track page view on mount
  useEffect(() => {
    analytics.initialize();
    analytics.trackEvent(AnalyticsEvent.LANDING_PAGE_VIEWED, {
      ...getUtmParams(),
      referrer: Platform.OS === 'web' ? document.referrer : undefined,
    });

    return () => {
      const timeSpent = Math.floor((Date.now() - pageLoadTime.current) / 1000);
      analytics.trackEvent(AnalyticsEvent.LANDING_TIME_SPENT, {
        seconds: timeSpent,
        ...getUtmParams(),
      });
    };
  }, [getUtmParams]);

  const trackCta = useCallback((ctaName: string, section: string) => {
    analytics.trackEvent(AnalyticsEvent.LANDING_CTA_CLICKED, {
      cta_name: ctaName,
      section,
      ...getUtmParams(),
    });
  }, [getUtmParams]);

  const handleGetStarted = () => {
    trackCta('get_started', 'hero');
    if (Platform.OS === 'web' && isLandingDomain) {
      window.location.href = `${appUrl}/signup`;
      return;
    }
    navigation.navigate('SignUp');
  };

  const handleLogin = () => {
    trackCta('login', 'navbar');
    if (Platform.OS === 'web' && isLandingDomain) {
      window.location.href = `${appUrl}/login`;
      return;
    }
    navigation.navigate('Login');
  };

  return (
    <View style={styles.container}>
      {/* Fixed Navigation */}
      <View style={[styles.navbar, isDesktop && styles.navbarDesktop]}>
        <View style={styles.logoContainer}>
          <View style={styles.logoIcon}>
            <Text style={styles.logoEmoji}>🤖</Text>
          </View>
          <Text style={styles.logoText}>
            Wealfi
          </Text>
        </View>

        <View style={styles.navActions}>
          {isDesktop && (
            <>
              <Pressable onPress={() => { if (Platform.OS === 'web') window.location.href = '/fr/tools'; }} style={styles.navLinkButton}>
                <Text style={styles.navLinkText}>Outils</Text>
              </Pressable>
              <Pressable onPress={handleLogin} style={styles.navLinkButton}>
                <Text style={styles.navLinkText}>Connexion</Text>
              </Pressable>
            </>
          )}
          <Pressable onPress={handleGetStarted} style={[styles.ctaButton, !isDesktop && styles.ctaButtonMobile]}>
            <Text style={styles.ctaButtonText}>{isDesktop ? 'Diagnostic gratuit' : 'Essayer'}</Text>
          </Pressable>
        </View>
      </View>

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        {/* Hero Section */}
        <View style={[styles.heroSection, isDesktop && styles.heroSectionDesktop]}>
          <View style={[styles.heroContent, isDesktop && styles.heroContentDesktop]}>
            <View style={[styles.heroLeft, isDesktop && styles.heroLeftDesktop]}>
              <View style={styles.heroBadge}>
                <View style={styles.heroBadgeDot} />
                <Text style={styles.heroBadgeText}>Pour les jeunes pros qui veulent faire bosser leur argent</Text>
              </View>

              <Text style={[styles.heroTitle, isDesktop && styles.heroTitleDesktop]}>
                Tu gagnes bien ta vie.{'\n'}
                <Text style={styles.heroTitleAccent}>Fais-la fructifier.</Text>
              </Text>

              <Text style={styles.heroSubtitle}>
                3 minutes pour savoir si ton épargne dort ou travaille.
                Plan d'action personnalisé. Sans jargon.
              </Text>

              <Pressable onPress={handleGetStarted} style={styles.primaryButton}>
                <Text style={styles.primaryButtonText}>Faire mon diagnostic gratuit</Text>
                <Icon name="arrow-right" size={20} color={brandColors.white} />
              </Pressable>

              <View style={styles.heroTrust}>
                <View style={styles.trustItem}>
                  <Icon name="check-circle" size={18} color={brandColors.green} />
                  <Text style={styles.trustText}>100% gratuit</Text>
                </View>
                <View style={styles.trustItem}>
                  <Icon name="clock-outline" size={18} color={brandColors.green} />
                  <Text style={styles.trustText}>3 minutes</Text>
                </View>
                <View style={styles.trustItem}>
                  <Icon name="shield-lock" size={18} color={brandColors.green} />
                  <Text style={styles.trustText}>Sans connexion bancaire</Text>
                </View>
              </View>
            </View>

            {/* Chat Preview - Desktop only */}
            {isDesktop && (
              <View style={styles.heroRight}>
                <View style={styles.chatPreview}>
                  <View style={styles.chatHeader}>
                    <View style={styles.chatAvatar}>
                      <Text style={styles.chatAvatarText}>🤖</Text>
                    </View>
                    <View>
                      <Text style={styles.chatName}>Wealfi</Text>
                      <View style={styles.chatStatus}>
                        <View style={styles.chatStatusDot} />
                        <Text style={styles.chatStatusText}>Analyse en cours...</Text>
                      </View>
                    </View>
                  </View>

                  <View style={styles.chatMessages}>
                    <View style={styles.chatBubbleAi}>
                      <Text style={styles.chatBubbleText}>
                        Salut {PERSONA.name} ! J'ai analysé ton profil. Tu as 12 000€ sur ton Livret A à 3%...
                      </Text>
                    </View>
                    <View style={styles.chatBubbleAi}>
                      <Text style={styles.chatBubbleText}>
                        Avec un <Text style={styles.chatBold}>PEA investi en ETF</Text>, tu aurais pu gagner <Text style={styles.chatBold}>plusieurs centaines d'euros</Text> de plus.
                      </Text>
                    </View>
                    <View style={styles.chatBubbleAi}>
                      <Text style={styles.chatBubbleText}>
                        Je t'explique comment faire en 3 étapes ?
                      </Text>
                    </View>
                  </View>
                </View>
              </View>
            )}
          </View>
        </View>

        {/* Recognition Section */}
        <View style={styles.recognitionSection}>
          <Text style={styles.sectionTitle}>Tu te reconnais ?</Text>
          <View style={[styles.recognitionGrid, isDesktop && styles.recognitionGridDesktop]}>
            {recognitionItems.map((item, index) => (
              <View key={index} style={styles.recognitionItem}>
                <Icon name="checkbox-marked-circle" size={24} color={brandColors.green} />
                <Text style={styles.recognitionText}>{item}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Pain Points Section */}
        <View style={styles.painSection}>
          <Text style={styles.sectionTitle}>Le vrai problème</Text>
          <View style={[styles.painGrid, isDesktop && styles.painGridDesktop]}>
            {painPoints.map((pain, index) => (
              <View key={index} style={styles.painCard}>
                <Text style={styles.painEmoji}>{pain.emoji}</Text>
                <Text style={styles.painTitle}>{pain.title}</Text>
                <Text style={styles.painDesc}>{pain.description}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Compound Interest Callout */}
        <View style={styles.compoundSection}>
          <View style={[styles.compoundCard, isDesktop && styles.compoundCardDesktop]}>
            <Text style={styles.compoundTitle}>Le pouvoir des intérêts composés</Text>
            <View style={styles.compoundHighlight}>
              <Text style={styles.compoundAmount}>50€/mois</Text>
              <Text style={styles.compoundPeriod}>pendant 20 ans à 7%/an</Text>
              <Text style={styles.compoundEquals}>=</Text>
              <Text style={styles.compoundResult}>26 000€</Text>
            </View>
            <Text style={styles.compoundDetail}>
              dont 14 000€ d'intérêts. Tu n'as mis que 12 000€ de ta poche.
            </Text>
            <Text style={styles.compoundDisclaimer}>
              Simulation à titre indicatif. Les performances passées ne préjugent pas des performances futures.
            </Text>
          </View>
        </View>

        {/* Solution Section */}
        <View style={styles.solutionSection}>
          <Text style={styles.sectionTitle}>
            Wealfi fait le travail <Text style={styles.titleAccent}>pour toi</Text>
          </Text>
          <Text style={styles.sectionSubtitle}>
            Pas besoin de devenir expert. Wealfi analyse, explique et te guide.
          </Text>

          <View style={[styles.featuresGrid, isDesktop && styles.featuresGridDesktop]}>
            {features.map((feature, index) => (
              <View key={index} style={styles.featureCard}>
                <View style={styles.featureIconContainer}>
                  <Icon name={feature.icon as any} size={32} color={brandColors.green} />
                </View>
                <Text style={styles.featureTitle}>{feature.title}</Text>
                <Text style={styles.featureDesc}>{feature.desc}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Social Proof Section */}
        <View style={styles.socialProofSection}>
          <Text style={styles.sectionTitle}>Ils l'ont fait</Text>
          <View style={[styles.testimonialsGrid, isDesktop && styles.testimonialsGridDesktop]}>
            {testimonials.map((t, index) => (
              <View key={index} style={styles.testimonialCard}>
                <View style={styles.testimonialHeader}>
                  <View style={styles.testimonialAvatar}>
                    <Text style={styles.testimonialAvatarText}>{t.name.charAt(0)}</Text>
                  </View>
                  <View style={styles.testimonialInfo}>
                    <Text style={styles.testimonialName}>{t.name}</Text>
                    <Text style={styles.testimonialRole}>{t.role}</Text>
                  </View>
                  <View style={styles.testimonialSavings}>
                    <Text style={styles.testimonialSavingsValue}>{t.savings}</Text>
                  </View>
                </View>
                <Text style={styles.testimonialText}>"{t.text}"</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Trust Section */}
        <View style={styles.trustSection}>
          <Text style={styles.sectionTitle}>
            Tes données sont <Text style={styles.titleAccent}>protégées</Text>
          </Text>

          <View style={[styles.trustGrid, isDesktop && styles.trustGridDesktop]}>
            {[
              {
                icon: 'lock',
                title: 'Connexion sécurisée',
                desc: 'Tes échanges sont protégés par chiffrement SSL/TLS',
              },
              {
                icon: 'hand-back-right',
                title: 'Données jamais vendues',
                desc: 'Pas de pub ciblée, pas de revente à des tiers',
              },
              {
                icon: 'bank-outline',
                title: 'Connexion bancaire optionnelle',
                desc: 'Via Powens, établissement de paiement agréé ACPR (n°16948)',
              },
              {
                icon: 'delete-outline',
                title: 'Supprime ton compte à tout moment',
                desc: 'Un clic pour supprimer toutes tes données',
              },
            ].map((card, index) => (
              <View key={index} style={[styles.trustCard, isDesktop && styles.trustCardDesktop]}>
                <View style={styles.trustCardIcon}>
                  <Icon name={card.icon as any} size={28} color={brandColors.green} />
                </View>
                <Text style={styles.trustCardTitle}>{card.title}</Text>
                <Text style={styles.trustCardDesc}>{card.desc}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* FAQ Section */}
        <View style={styles.faqSection}>
          <Text style={styles.sectionTitle}>Questions fréquentes</Text>
          <View style={styles.faqGrid}>
            {faqItems.map((faq, index) => (
              <View key={index} style={styles.faqCard}>
                <Text style={styles.faqQuestion}>{faq.q}</Text>
                <Text style={styles.faqAnswer}>{faq.a}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Pricing Section */}
        <View style={styles.pricingSection}>
          <Text style={styles.sectionTitle}>Tarif simple</Text>
          <View style={styles.pricingCard}>
            <View style={styles.earlyBadge}>
              <Text style={styles.earlyBadgeText}>🎉 EARLY ACCESS -20%</Text>
            </View>
            <Text style={styles.pricingLabel}>Premium</Text>
            <View style={styles.priceRow}>
              <Text style={styles.priceOriginal}>12,99€</Text>
              <Text style={styles.priceValue}>9,99€</Text>
              <Text style={styles.priceUnit}>/mois</Text>
            </View>
            <Text style={styles.priceSubtext}>Remboursé dès la 1ère optimisation trouvée</Text>

            <View style={styles.featuresList}>
              {premiumFeatures.map((feat, i) => (
                <View key={i} style={styles.featureItem}>
                  <Text style={styles.featureCheck}>✓</Text>
                  <Text style={styles.featureText}>{feat}</Text>
                </View>
              ))}
            </View>

            <Pressable
              style={styles.pricingCta}
              onPress={() => { trackCta('start_premium', 'pricing'); handleGetStarted(); }}
            >
              <Text style={styles.pricingCtaText}>Commencer gratuitement</Text>
            </Pressable>
            <Text style={styles.pricingNote}>Le diagnostic est gratuit. Premium après si tu veux.</Text>
            <Text style={styles.pricingDisclaimer}>
              Wealfi est un outil d'aide à la décision. Ce n'est pas un service de conseil en investissement au sens de l'AMF.
            </Text>
          </View>
        </View>

        {/* Final CTA */}
        <View style={styles.finalCtaSection}>
          <Text style={styles.finalCtaTitle}>
            3 minutes maintenant.{'\n'}Des années d'économies.
          </Text>
          <Pressable
            style={styles.finalCtaButton}
            onPress={() => { trackCta('final_cta', 'footer'); handleGetStarted(); }}
          >
            <Text style={styles.finalCtaButtonText}>Faire mon diagnostic gratuit</Text>
            <Icon name="arrow-right" size={20} color={brandColors.dark} />
          </Pressable>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerLogo}>
            Wealfi
          </Text>
          <Text style={styles.footerTagline}>
            Ton assistant financier IA
          </Text>
          <View style={styles.footerToolsSection}>
            <Text style={styles.footerToolsTitle}>Outils Gratuits</Text>
            <Pressable onPress={() => { if (Platform.OS === 'web') window.location.href = '/fr/tools'; }}>
              <Text style={styles.footerToolsLink}>Tous les outils</Text>
            </Pressable>
            <Pressable onPress={() => { if (Platform.OS === 'web') window.location.href = '/fr/tools/compound-interest-calculator'; }}>
              <Text style={styles.footerToolsLink}>Intérêts Composés</Text>
            </Pressable>
          </View>
          <Text style={styles.footerDisclaimer}>
            © 2026 Wealfi • Pas un service de conseil en investissement au sens de l'AMF
          </Text>
        </View>
      </ScrollView>

    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: brandColors.white,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: 70,
  },

  // Navbar
  navbar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  navbarDesktop: {
    paddingHorizontal: 48,
    paddingVertical: 16,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  logoIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: brandColors.greenLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoEmoji: {
    fontSize: 20,
  },
  logoText: {
    fontSize: 20,
    fontWeight: '700',
    color: brandColors.dark,
  },
  logoTextAccent: {
    color: brandColors.green,
  },
  navActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  navLinkButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  navLinkText: {
    fontSize: 14,
    fontWeight: '500',
    color: brandColors.gray,
  },
  ctaButton: {
    backgroundColor: brandColors.green,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 24,
  },
  ctaButtonMobile: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  ctaButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: brandColors.white,
  },

  // Hero
  heroSection: {
    paddingHorizontal: 16,
    paddingVertical: 48,
    backgroundColor: brandColors.white,
  },
  heroSectionDesktop: {
    paddingHorizontal: 48,
    paddingVertical: 80,
  },
  heroContent: {
    maxWidth: 1200,
    marginHorizontal: 'auto',
    width: '100%',
  },
  heroContentDesktop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 48,
  },
  heroLeft: {
    flex: 1,
    alignItems: 'center',
  },
  heroLeftDesktop: {
    alignItems: 'flex-start',
  },
  heroRight: {
    flex: 1,
  },
  heroBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: brandColors.greenLight,
    borderRadius: 24,
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginBottom: 24,
    gap: 8,
  },
  heroBadgeDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: brandColors.green,
  },
  heroBadgeText: {
    fontSize: 14,
    fontWeight: '500',
    color: brandColors.dark,
  },
  heroTitle: {
    fontSize: 32,
    fontWeight: '700',
    color: brandColors.dark,
    textAlign: 'center',
    lineHeight: 40,
    marginBottom: 16,
  },
  heroTitleDesktop: {
    fontSize: 48,
    lineHeight: 56,
    textAlign: 'left',
  },
  heroTitleAccent: {
    color: brandColors.green,
  },
  heroSubtitle: {
    fontSize: 16,
    color: brandColors.gray,
    textAlign: 'center',
    lineHeight: 26,
    marginBottom: 32,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: brandColors.green,
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 32,
    gap: 8,
  },
  primaryButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: brandColors.white,
  },
  heroTrust: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 16,
    marginTop: 24,
  },
  trustItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  trustText: {
    fontSize: 13,
    color: brandColors.gray,
  },

  // Chat Preview
  chatPreview: {
    backgroundColor: brandColors.dark,
    borderRadius: 20,
    padding: 24,
  },
  chatHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
    marginBottom: 16,
  },
  chatAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: brandColors.green,
    justifyContent: 'center',
    alignItems: 'center',
  },
  chatAvatarText: {
    fontSize: 18,
  },
  chatName: {
    fontSize: 14,
    fontWeight: '600',
    color: brandColors.white,
  },
  chatStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  chatStatusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: brandColors.green,
  },
  chatStatusText: {
    fontSize: 12,
    color: brandColors.green,
  },
  chatMessages: {
    gap: 12,
  },
  chatBubbleAi: {
    backgroundColor: brandColors.green,
    borderRadius: 16,
    borderBottomLeftRadius: 4,
    padding: 12,
    maxWidth: '90%',
  },
  chatBubbleText: {
    fontSize: 14,
    color: brandColors.white,
    lineHeight: 20,
  },
  chatBold: {
    fontWeight: '700',
  },

  // Recognition Section
  recognitionSection: {
    backgroundColor: '#F9FAFB',
    paddingHorizontal: 16,
    paddingVertical: 48,
    alignItems: 'center',
  },
  recognitionGrid: {
    width: '100%',
    maxWidth: 600,
    gap: 16,
  },
  recognitionGridDesktop: {
    maxWidth: 800,
  },
  recognitionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: brandColors.white,
    padding: 16,
    borderRadius: 12,
  },
  recognitionText: {
    flex: 1,
    fontSize: 15,
    color: brandColors.dark,
    lineHeight: 22,
  },

  // Pain Section
  painSection: {
    paddingHorizontal: 16,
    paddingVertical: 48,
    alignItems: 'center',
  },
  painGrid: {
    width: '100%',
    maxWidth: 1000,
    gap: 16,
  },
  painGridDesktop: {
    flexDirection: 'row',
  },
  painCard: {
    flex: 1,
    backgroundColor: '#FEF2F2',
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
  },
  painEmoji: {
    fontSize: 40,
    marginBottom: 12,
  },
  painTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 8,
    textAlign: 'center',
  },
  painDesc: {
    fontSize: 14,
    color: brandColors.gray,
    textAlign: 'center',
    lineHeight: 22,
  },

  // Compound Interest Callout
  compoundSection: {
    paddingHorizontal: 16,
    paddingVertical: 32,
    alignItems: 'center',
    backgroundColor: brandColors.greenLight,
  },
  compoundCard: {
    backgroundColor: brandColors.white,
    borderRadius: 20,
    padding: 32,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: brandColors.green,
    width: '100%',
  },
  compoundCardDesktop: {
    maxWidth: 500,
  },
  compoundTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 20,
    textAlign: 'center',
  },
  compoundHighlight: {
    alignItems: 'center',
    marginBottom: 16,
  },
  compoundAmount: {
    fontSize: 32,
    fontWeight: '800',
    color: brandColors.green,
  },
  compoundPeriod: {
    fontSize: 14,
    color: brandColors.gray,
    marginTop: 4,
  },
  compoundEquals: {
    fontSize: 24,
    fontWeight: '700',
    color: brandColors.gray,
    marginVertical: 8,
  },
  compoundResult: {
    fontSize: 40,
    fontWeight: '800',
    color: brandColors.green,
  },
  compoundDetail: {
    fontSize: 16,
    color: brandColors.dark,
    textAlign: 'center',
    fontWeight: '600',
    marginBottom: 12,
  },
  compoundDisclaimer: {
    fontSize: 11,
    color: brandColors.gray,
    textAlign: 'center',
    fontStyle: 'italic',
  },

  // Solution Section
  solutionSection: {
    backgroundColor: '#F9FAFB',
    paddingHorizontal: 16,
    paddingVertical: 48,
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: brandColors.dark,
    textAlign: 'center',
    marginBottom: 12,
  },
  sectionSubtitle: {
    fontSize: 16,
    color: brandColors.gray,
    textAlign: 'center',
    marginBottom: 32,
    maxWidth: 600,
  },
  titleAccent: {
    color: brandColors.green,
  },
  featuresGrid: {
    width: '100%',
    maxWidth: 1000,
    gap: 16,
  },
  featuresGridDesktop: {
    flexDirection: 'row',
  },
  featureCard: {
    flex: 1,
    backgroundColor: brandColors.white,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
  },
  featureIconContainer: {
    width: 64,
    height: 64,
    borderRadius: 16,
    backgroundColor: brandColors.greenLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  featureTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 8,
    textAlign: 'center',
  },
  featureDesc: {
    fontSize: 14,
    color: brandColors.gray,
    textAlign: 'center',
    lineHeight: 22,
  },

  // Social Proof
  socialProofSection: {
    paddingHorizontal: 16,
    paddingVertical: 48,
    alignItems: 'center',
  },
  testimonialsGrid: {
    width: '100%',
    maxWidth: 1000,
    gap: 16,
  },
  testimonialsGridDesktop: {
    flexDirection: 'row',
  },
  testimonialCard: {
    flex: 1,
    backgroundColor: brandColors.white,
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  testimonialHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  testimonialAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: brandColors.green,
    justifyContent: 'center',
    alignItems: 'center',
  },
  testimonialAvatarText: {
    fontSize: 18,
    fontWeight: '700',
    color: brandColors.white,
  },
  testimonialInfo: {
    flex: 1,
  },
  testimonialName: {
    fontSize: 15,
    fontWeight: '600',
    color: brandColors.dark,
  },
  testimonialRole: {
    fontSize: 13,
    color: brandColors.gray,
  },
  testimonialSavings: {
    backgroundColor: brandColors.greenLight,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
  },
  testimonialSavingsValue: {
    fontSize: 13,
    fontWeight: '600',
    color: brandColors.green,
  },
  testimonialText: {
    fontSize: 15,
    color: brandColors.gray,
    lineHeight: 24,
    fontStyle: 'italic',
  },

  // FAQ
  faqSection: {
    backgroundColor: '#F9FAFB',
    paddingHorizontal: 16,
    paddingVertical: 48,
    alignItems: 'center',
  },
  faqGrid: {
    width: '100%',
    maxWidth: 700,
    gap: 16,
  },
  faqCard: {
    backgroundColor: brandColors.white,
    borderRadius: 12,
    padding: 20,
  },
  faqQuestion: {
    fontSize: 16,
    fontWeight: '600',
    color: brandColors.dark,
    marginBottom: 8,
  },
  faqAnswer: {
    fontSize: 14,
    color: brandColors.gray,
    lineHeight: 22,
  },

  // Pricing
  pricingSection: {
    paddingHorizontal: 16,
    paddingVertical: 48,
    alignItems: 'center',
  },
  pricingCard: {
    backgroundColor: brandColors.white,
    borderRadius: 20,
    padding: 32,
    width: '100%',
    maxWidth: 400,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: brandColors.green,
  },
  earlyBadge: {
    backgroundColor: brandColors.orange,
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 16,
    marginBottom: 16,
  },
  earlyBadgeText: {
    fontSize: 13,
    fontWeight: '600',
    color: brandColors.white,
  },
  pricingLabel: {
    fontSize: 24,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 8,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 8,
    marginBottom: 8,
  },
  priceOriginal: {
    fontSize: 18,
    color: brandColors.gray,
    textDecorationLine: 'line-through',
  },
  priceValue: {
    fontSize: 48,
    fontWeight: '700',
    color: brandColors.green,
  },
  priceUnit: {
    fontSize: 18,
    color: brandColors.gray,
  },
  priceSubtext: {
    fontSize: 14,
    color: brandColors.gray,
    marginBottom: 24,
  },
  featuresList: {
    width: '100%',
    gap: 12,
    marginBottom: 24,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  featureCheck: {
    fontSize: 16,
    color: brandColors.green,
    fontWeight: '600',
  },
  featureText: {
    fontSize: 15,
    color: brandColors.dark,
  },
  pricingCta: {
    backgroundColor: brandColors.green,
    paddingHorizontal: 32,
    paddingVertical: 14,
    borderRadius: 32,
    width: '100%',
    alignItems: 'center',
    marginBottom: 12,
  },
  pricingCtaText: {
    fontSize: 16,
    fontWeight: '600',
    color: brandColors.white,
  },
  pricingNote: {
    fontSize: 13,
    color: brandColors.gray,
    textAlign: 'center',
  },
  pricingDisclaimer: {
    fontSize: 12,
    color: brandColors.gray,
    textAlign: 'center',
    marginTop: 16,
    fontStyle: 'italic',
    paddingHorizontal: 24,
    lineHeight: 18,
  },

  // Final CTA
  finalCtaSection: {
    backgroundColor: brandColors.green,
    paddingHorizontal: 16,
    paddingVertical: 64,
    alignItems: 'center',
  },
  finalCtaTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: brandColors.white,
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 36,
  },
  finalCtaButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: brandColors.white,
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 32,
    gap: 8,
  },
  finalCtaButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: brandColors.dark,
  },

  // Trust Section
  trustSection: {
    paddingHorizontal: 16,
    paddingVertical: 48,
    backgroundColor: brandColors.greenLight,
  },
  trustGrid: {
    marginTop: 24,
    gap: 16,
  },
  trustGridDesktop: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 20,
  },
  trustCard: {
    backgroundColor: brandColors.white,
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  trustCardDesktop: {
    flex: 1,
    minWidth: 220,
    maxWidth: 280,
  },
  trustCardIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: brandColors.greenLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  trustCardTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 8,
  },
  trustCardDesc: {
    fontSize: 14,
    color: brandColors.gray,
    lineHeight: 20,
  },

  // Footer
  footer: {
    backgroundColor: brandColors.dark,
    paddingHorizontal: 16,
    paddingVertical: 32,
    alignItems: 'center',
  },
  footerLogo: {
    fontSize: 20,
    fontWeight: '700',
    color: brandColors.white,
    marginBottom: 8,
  },
  footerTagline: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.6)',
    marginBottom: 16,
  },
  footerToolsSection: {
    alignItems: 'center',
    marginBottom: 16,
    gap: 6,
  },
  footerToolsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: 'rgba(255,255,255,0.8)',
    marginBottom: 4,
  },
  footerToolsLink: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.5)',
  },
  footerDisclaimer: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.4)',
  },
});
